import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Package, PauseCircle, LogOut, AlertCircle, Download, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "safemama.admin.token";

const PLAN_LABEL = {
  single: "Single Edition · ₹699",
  "3month": "3-Month · ₹1,799",
  "6month": "6-Month · ₹2,999",
};

const STATUS_STYLES = {
  active: "bg-sage/15 text-sage border-sage/30",
  pending: "bg-blush/25 text-terracotta border-terracotta/30",
  paused: "bg-ink/10 text-ink/70 border-ink/20",
  completed: "bg-ink/10 text-ink/60 border-ink/20",
};

function LoginScreen({ onLogin }) {
  const [token, setToken] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (token.trim()) onLogin(token.trim());
  };
  return (
    <div className="min-h-[70vh] flex items-center" data-testid="admin-login">
      <div className="editorial-container w-full">
        <form className="max-w-md" onSubmit={submit}>
          <div className="overline text-ink/60">Editorial · Admin</div>
          <h1 className="mt-4 font-serif italic text-5xl md:text-6xl text-terracotta">Sign in.</h1>
          <p className="mt-6 font-serif text-lg text-ink/70">
            Enter your admin token to view subscribers.
          </p>
          <label className="overline text-ink/60 block mt-10 mb-3">Admin token</label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
            placeholder="••••••••••"
            data-testid="admin-token-input"
            autoFocus
          />
          <button
            type="submit"
            className="btn-pill mt-10"
            data-testid="admin-signin-btn"
          >
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

function AddressCell({ a, name, phone, email }) {
  if (!a) {
    return (
      <div className="font-serif text-sm text-ink/60">
        <div>{email}</div>
        {phone && <div>{phone}</div>}
        <div className="italic text-ink/40 mt-1">No address on file</div>
      </div>
    );
  }
  return (
    <div className="font-serif text-sm text-ink/80 leading-snug">
      <div className="font-medium text-ink">{name || "—"}</div>
      <div>{a.line1}</div>
      {a.line2 && <div>{a.line2}</div>}
      <div>
        {a.city}, {a.state} {a.pincode}
      </div>
      <div className="mt-1 text-ink/60">{email}</div>
      {phone && <div className="text-ink/60">{phone}</div>}
    </div>
  );
}

export default function Admin() {
  const search = useLocation().search;
  const [token, setToken] = useState(() => {
    const qp = new URLSearchParams(search).get("token");
    if (qp) {
      localStorage.setItem(TOKEN_KEY, qp);
      return qp;
    }
    return localStorage.getItem(TOKEN_KEY) || "";
  });
  const [subs, setSubs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  // First Envelope campaign state
  const [registrations, setRegistrations] = useState([]);
  const [waitlistReg, setWaitlistReg] = useState([]);
  const [regSummary, setRegSummary] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  const [regOpen, setRegOpen] = useState(true);

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/subscribers`, auth());
      setSubs(res.data.subscribers);
      setSummary(res.data.summary);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Invalid token.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Could not load subscribers.");
      }
    } finally {
      setLoading(false);
    }
    loadWaitlist();
    loadRegistrations();
  };

  // ---- First Envelope campaign admin ----
  const loadRegistrations = async () => {
    if (!token) return;
    setRegLoading(true);
    try {
      const [listRes, setRes] = await Promise.all([
        axios.get(`${API}/admin/first-envelope`, auth()),
        axios.get(`${API}/admin/first-envelope/settings`, auth()),
      ]);
      setRegistrations(listRes.data.confirmed || []);
      setWaitlistReg(listRes.data.waitlist || []);
      setRegSummary(listRes.data.summary || null);
      setRegOpen(setRes.data.settings?.open !== false);
    } catch {
      // silent
    } finally {
      setRegLoading(false);
    }
  };

  const updateFulfilment = async (rid, status) => {
    try {
      await axios.post(`${API}/admin/first-envelope/${rid}/fulfilment`, { status }, auth());
      setRegistrations((rs) => rs.map((r) => (r.id === rid ? { ...r, fulfilment_status: status } : r)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Could not update fulfilment status.");
    }
  };

  const saveRegSettings = async (patch) => {
    try {
      const res = await axios.post(`${API}/admin/first-envelope/settings`, patch, auth());
      setRegOpen(res.data.settings?.open !== false);
      setRegSummary((s) => (s ? { ...s, mode: res.data.state.mode } : s));
      toast.success("Campaign settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings.");
    }
  };

  const toggleInternal = async (rid, is_internal) => {
    try {
      await axios.post(`${API}/admin/first-envelope/${rid}/internal`, { is_internal }, auth());
      toast.success(is_internal ? "Marked internal." : "Unmarked internal.");
      loadRegistrations();
    } catch {
      toast.error("Could not update.");
    }
  };

  const approveWaitlist = async (rid) => {
    try {
      await axios.post(`${API}/admin/first-envelope/${rid}/approve`, {}, auth());
      toast.success("Approved for a free envelope.");
      loadRegistrations();
    } catch {
      toast.error("Could not approve.");
    }
  };

  const exportRegistrationsCsv = async (type = "confirmed") => {
    try {
      const res = await axios.get(`${API}/admin/first-envelope.csv?type=${type}`, { ...auth(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `safemama-first-envelope-${type}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error("Could not export CSV.");
    }
  };

  const loadWaitlist = async () => {
    if (!token) return;
    setWaitlistLoading(true);
    try {
      const res = await axios.get(`${API}/admin/waitlist`, auth());
      setWaitlist(res.data.entries || []);
    } catch {
      // silent — Subscribers already showed a global error if auth failed
    } finally {
      setWaitlistLoading(false);
    }
  };

  const exportWaitlistCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist.csv`, { ...auth(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "safemama-waitlist.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error("Could not export CSV.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onLogin = (t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSubs([]);
  };

  const markDispatched = async (sid) => {
    try {
      await axios.post(`${API}/admin/subscribers/${sid}/mark-dispatched`, { delta: 1 }, auth());
      toast.success("Marked as dispatched.");
      load();
    } catch {
      toast.error("Could not update.");
    }
  };

  const setStatus = async (sid, status) => {
    try {
      await axios.post(`${API}/admin/subscribers/${sid}/status`, { status }, auth());
      toast.success(`Marked ${status}.`);
      load();
    } catch {
      toast.error("Could not update.");
    }
  };

  if (!token) return <LoginScreen onLogin={onLogin} />;

  const visible = filter === "all" ? subs : subs.filter((s) => s.status === filter);

  return (
    <div className="pt-8 pb-32" data-testid="admin-page">
      <section className="editorial-container">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="overline text-ink/60">Editorial · Admin</div>
            <h1 className="mt-3 font-serif text-5xl md:text-6xl leading-tight text-ink">
              Subscribers
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="btn-pill ghost" data-testid="admin-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={onLogout} className="btn-pill ghost" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-ink/15 pt-6">
          <div>
            <div className="overline text-ink/50">Total</div>
            <div className="font-serif text-4xl text-ink mt-2">{summary.total}</div>
          </div>
          <div>
            <div className="overline text-ink/50">Active</div>
            <div className="font-serif text-4xl text-sage mt-2">{summary.active}</div>
          </div>
          <div>
            <div className="overline text-ink/50">Paused</div>
            <div className="font-serif text-4xl text-ink/70 mt-2">{summary.paused || 0}</div>
          </div>
          <div>
            <div className="overline text-ink/50">Completed</div>
            <div className="font-serif text-4xl text-ink/60 mt-2">{summary.completed || 0}</div>
          </div>
        </div>

        {/* Filter */}
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-ink/15 pt-6" data-testid="admin-filters">
          {["all", "active", "paused", "completed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`overline transition-colors ${
                filter === f ? "text-ink" : "text-ink/40 hover:text-ink/70"
              }`}
              data-testid={`admin-filter-${f}`}
            >
              {f} {filter === f && subs && `(${visible.length})`}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-8 overflow-x-auto">
          <table className="w-full font-serif text-base border-collapse" data-testid="admin-table">
            <thead>
              <tr className="text-left border-b border-ink/25">
                <th className="overline text-ink/60 py-4 pr-6 font-normal">Reader</th>
                <th className="overline text-ink/60 py-4 pr-6 font-normal">Edition</th>
                <th className="overline text-ink/60 py-4 pr-6 font-normal">Plan</th>
                <th className="overline text-ink/60 py-4 pr-6 font-normal">Status</th>
                <th className="overline text-ink/60 py-4 pr-6 font-normal">Editions</th>
                <th className="overline text-ink/60 py-4 pr-6 font-normal">Delivery</th>
                <th className="overline text-ink/60 py-4 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="py-12 text-center italic text-ink/50">Loading…</td></tr>
              )}
              {!loading && visible.length === 0 && (
                <tr><td colSpan="7" className="py-12 text-center italic text-ink/50">No subscribers yet.</td></tr>
              )}
              {!loading && visible.map((s) => (
                <tr key={s.id} className="border-b border-ink/10 align-top" data-testid={`admin-row-${s.id}`}>
                  <td className="py-6 pr-6">
                    <div className="font-serif italic text-lg text-ink">{s.name || "—"}</div>
                    <div className="text-sm text-ink/60 mt-1">{s.email}</div>
                    <div className="text-xs text-ink/40 mt-2 uppercase tracking-wider">
                      {new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  </td>
                  <td className="py-6 pr-6">
                    <div
                      className={`inline-block font-serif italic text-lg ${
                        s.edition === "motherhood" ? "text-sage" : "text-terracotta"
                      }`}
                    >
                      {s.edition === "motherhood" ? "Motherhood" : "Pregnancy"}
                    </div>
                    {s.recent_change?.kind === "edition" && (
                      <div className="mt-2 flex items-start gap-1 text-xs text-terracotta font-serif italic max-w-[16ch]" title={JSON.stringify(s.recent_change)}>
                        <AlertCircle size={12} className="mt-0.5 shrink-0" />
                        <span>Changed from {s.recent_change.from}. Send {s.edition} next.</span>
                      </div>
                    )}
                    {s.recent_change?.kind === "address" && (
                      <div className="mt-2 flex items-start gap-1 text-xs text-terracotta font-serif italic max-w-[16ch]">
                        <AlertCircle size={12} className="mt-0.5 shrink-0" />
                        <span>New address. Update label.</span>
                      </div>
                    )}
                    {s.recent_change?.kind === "preferences" && (
                      <div className="mt-2 flex items-start gap-1 text-xs text-terracotta font-serif italic max-w-[16ch]">
                        <AlertCircle size={12} className="mt-0.5 shrink-0" />
                        <span>Preferences updated.</span>
                      </div>
                    )}
                    {s.preferences && (
                      <div className="mt-3 text-xs text-ink/60 font-sans italic max-w-[18ch] line-clamp-3">
                        “{s.preferences}”
                      </div>
                    )}
                  </td>
                  <td className="py-6 pr-6 text-sm text-ink/80">
                    {PLAN_LABEL[s.package_id] || s.package_id}
                    <div className="text-xs text-ink/40 mt-1 capitalize">{s.provider?.replace(/_/g, " ")}</div>
                  </td>
                  <td className="py-6 pr-6">
                    <span
                      className={`inline-block px-3 py-1 rounded-full border text-xs uppercase tracking-wider ${
                        STATUS_STYLES[s.status] || STATUS_STYLES.pending
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="py-6 pr-6">
                    <div className="font-serif text-2xl text-ink leading-none">
                      {s.editions_remaining}
                      <span className="text-ink/40"> / {s.total_editions}</span>
                    </div>
                    <div className="overline text-ink/50 mt-2">Remaining</div>
                    <div className="text-xs text-ink/50 mt-2">
                      {s.editions_dispatched} dispatched
                    </div>
                  </td>
                  <td className="py-6 pr-6 max-w-xs">
                    <AddressCell a={s.address} name={s.name} email={s.email} phone={s.phone} />
                  </td>
                  <td className="py-6 text-right">
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() => markDispatched(s.id)}
                        disabled={s.editions_remaining <= 0 || s.status === "paused"}
                        className="btn-pill disabled:opacity-40 py-2 px-4 text-[10px]"
                        data-testid={`dispatch-${s.id}`}
                        title={s.status === "paused" ? "Resume before dispatching" : "Mark next edition as dispatched"}
                      >
                        <Package size={12} /> Mark dispatched
                      </button>
                      {s.status === "active" && s.total_editions > 1 && (
                        <button
                          onClick={() => setStatus(s.id, "paused")}
                          className="btn-pill ghost py-2 px-4 text-[10px]"
                          data-testid={`pause-${s.id}`}
                        >
                          <PauseCircle size={12} /> Pause
                        </button>
                      )}
                      {s.status === "paused" && (
                        <button
                          onClick={() => setStatus(s.id, "active")}
                          className="btn-pill ghost py-2 px-4 text-[10px]"
                          data-testid={`resume-${s.id}`}
                        >
                          Resume
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Launch List (Waitlist) — populated while payments are paused pre-launch */}
      <section className="editorial-container mt-24" data-testid="admin-waitlist">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="overline text-ink/60">Editorial · Launch list</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-tight text-ink">
              Waitlist <span className="text-ink/40">· {waitlist.length}</span>
            </h2>
            <p className="mt-3 font-serif text-lg text-ink/70 max-w-xl">
              People who have asked to be notified when subscriptions open. Consented sign-ups only.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadWaitlist} className="btn-pill ghost" data-testid="waitlist-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={exportWaitlistCsv} className="btn-pill" data-testid="waitlist-export" disabled={!waitlist.length}>
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="text-left overline text-ink/60 border-b border-ink/15">
                <th className="py-4 pr-6">Name</th>
                <th className="py-4 pr-6">Email</th>
                <th className="py-4 pr-6">Interested edition</th>
                <th className="py-4 pr-6">Date joined</th>
              </tr>
            </thead>
            <tbody>
              {waitlistLoading && (
                <tr><td colSpan={4} className="py-8 text-center text-ink/50 font-serif italic">Loading…</td></tr>
              )}
              {!waitlistLoading && waitlist.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-ink/60 font-serif italic">
                    <Mail size={16} className="inline-block mr-2 -mt-0.5" />
                    No sign-ups yet.
                  </td>
                </tr>
              )}
              {waitlist.map((w) => (
                <tr key={w.id} className="border-b border-ink/10 align-top" data-testid={`waitlist-row-${w.id}`}>
                  <td className="py-4 pr-6 font-serif text-ink">{w.name}</td>
                  <td className="py-4 pr-6 font-serif text-ink/85">{w.email}</td>
                  <td className="py-4 pr-6 font-serif italic text-ink/85 capitalize">{w.edition}</td>
                  <td className="py-4 pr-6 font-serif text-sm text-ink/60">
                    {w.created_at ? new Date(w.created_at).toLocaleString() : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* First Envelope Registrations */}
      <section className="editorial-container mt-24" data-testid="admin-first-envelope">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="overline text-ink/60">Editorial · First envelope campaign</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-tight text-ink">
              Confirmed First Envelope <span className="text-ink/40">· {regSummary?.genuine_confirmed ?? 0}</span>
            </h2>
            <p className="mt-3 font-serif text-lg text-ink/70 max-w-xl">
              Genuine registered mothers. Internal test entries are still stored but excluded from this count. Waitlist appears below.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadRegistrations} className="btn-pill ghost" data-testid="reg-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={() => exportRegistrationsCsv("confirmed")} className="btn-pill" data-testid="reg-export" disabled={!registrations.length}>
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Campaign controls (open/closed toggle only — internal cap is server-side) */}
        <div className="mt-10 border border-ink/15 rounded-md px-6 py-6 md:px-8 flex flex-wrap items-center gap-x-10 gap-y-5" data-testid="reg-controls">
          <div className="flex items-center gap-4">
            <span className="overline text-ink/60">Registrations</span>
            <button
              onClick={() => saveRegSettings({ open: !regOpen })}
              className={`btn-pill ${regOpen ? "" : "ghost"}`}
              data-testid="reg-toggle-open"
            >
              {regOpen ? "Open" : "Closed"}
            </button>
            {regSummary?.mode && (
              <span className="text-sm font-serif italic text-ink/60" data-testid="reg-mode">
                {regSummary.mode === "waitlist" ? "First round full — waitlist active" : regSummary.mode === "closed" ? "Manually closed" : "Accepting registrations"}
              </span>
            )}
          </div>
          {regSummary && (
            <div className="flex items-center gap-6 text-sm font-serif text-ink/70">
              <span>Confirmed total <strong className="text-ink">{regSummary.confirmed_total ?? 0}</strong></span>
              <span>Internal <strong className="text-ink">{regSummary.internal_confirmed ?? 0}</strong></span>
              <span>Waitlist <strong className="text-ink">{regSummary.waitlist_total ?? 0}</strong></span>
              <span>Pending <strong className="text-ink">{regSummary.fulfilment?.pending ?? 0}</strong></span>
              <span>Packed <strong className="text-ink">{regSummary.fulfilment?.packed ?? 0}</strong></span>
              <span>Dispatched <strong className="text-ink">{regSummary.fulfilment?.dispatched ?? 0}</strong></span>
            </div>
          )}
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[1000px] border-collapse">
            <thead>
              <tr className="text-left overline text-ink/60 border-b border-ink/15">
                <th className="py-4 pr-6">Name</th>
                <th className="py-4 pr-6">Contact</th>
                <th className="py-4 pr-6">Address</th>
                <th className="py-4 pr-6">Stage</th>
                <th className="py-4 pr-6">Marketing</th>
                <th className="py-4 pr-6">Date</th>
                <th className="py-4 pr-6">Fulfilment</th>
                <th className="py-4 pr-6">Internal</th>
              </tr>
            </thead>
            <tbody>
              {regLoading && (
                <tr><td colSpan={8} className="py-8 text-center text-ink/50 font-serif italic">Loading…</td></tr>
              )}
              {!regLoading && registrations.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-ink/60 font-serif italic">
                    <Mail size={16} className="inline-block mr-2 -mt-0.5" />
                    No registrations yet.
                  </td>
                </tr>
              )}
              {registrations.map((r) => (
                <tr key={r.id} className="border-b border-ink/10 align-top" data-testid={`reg-row-${r.id}`}>
                  <td className="py-4 pr-6 font-serif text-ink">{r.name}</td>
                  <td className="py-4 pr-6 text-sm text-ink/80">
                    <div>{r.phone}</div>
                    <div className="text-ink/60">{r.email}</div>
                  </td>
                  <td className="py-4 pr-6 text-sm text-ink/80 max-w-[28ch]">
                    <div>{r.address_line1}{r.address_line2 ? `, ${r.address_line2}` : ""}</div>
                    <div className="text-ink/60">{r.city}, {r.state} — {r.pincode}</div>
                  </td>
                  <td className="py-4 pr-6 font-serif italic text-sm text-ink/80 capitalize">
                    {(r.motherhood_stage || "").replace(/_/g, " ")}
                  </td>
                  <td className="py-4 pr-6 text-sm">
                    <span className={r.marketing_consent ? "text-sage font-medium" : "text-ink/40"}>
                      {r.marketing_consent ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-4 pr-6 text-xs text-ink/60">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                  </td>
                  <td className="py-4 pr-6">
                    <select
                      value={r.fulfilment_status || "pending"}
                      onChange={(e) => updateFulfilment(r.id, e.target.value)}
                      className="bg-cream border border-ink/20 rounded px-2 py-1.5 font-serif text-sm text-ink cursor-pointer focus:border-ink outline-none capitalize"
                      data-testid={`reg-fulfilment-${r.id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="packed">Packed</option>
                      <option value="dispatched">Dispatched</option>
                    </select>
                  </td>
                  <td className="py-4 pr-6">
                    <button
                      onClick={() => toggleInternal(r.id, !r.is_internal)}
                      className={`btn-pill !py-1.5 !px-4 text-xs ${r.is_internal ? "" : "ghost"}`}
                      data-testid={`reg-internal-${r.id}`}
                      title={r.is_internal ? "Internal/test entry — excluded from genuine count" : "Mark as internal/test entry"}
                    >
                      {r.is_internal ? "Internal" : "Mark internal"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ---- Waitlist table ---- */}
        <div className="mt-16 flex items-end justify-between flex-wrap gap-6">
          <div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight text-ink">
              First Envelope Waitlist <span className="text-ink/40">· {regSummary?.waitlist_total ?? waitlistReg.length}</span>
            </h3>
            <p className="mt-3 font-serif text-lg text-ink/70 max-w-xl">
              Mothers who joined after the first round filled. Approving someone moves them into the confirmed list with a pending fulfilment status.
            </p>
          </div>
          <button onClick={() => exportRegistrationsCsv("waitlist")} className="btn-pill" data-testid="reg-waitlist-export" disabled={!waitlistReg.length}>
            <Download size={14} /> Export waitlist CSV
          </button>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[1000px] border-collapse">
            <thead>
              <tr className="text-left overline text-ink/60 border-b border-ink/15">
                <th className="py-4 pr-6">Name</th>
                <th className="py-4 pr-6">Contact</th>
                <th className="py-4 pr-6">Address</th>
                <th className="py-4 pr-6">Stage</th>
                <th className="py-4 pr-6">Contact consent</th>
                <th className="py-4 pr-6">Date</th>
                <th className="py-4 pr-6">Action</th>
              </tr>
            </thead>
            <tbody>
              {regLoading && (
                <tr><td colSpan={7} className="py-8 text-center text-ink/50 font-serif italic">Loading…</td></tr>
              )}
              {!regLoading && waitlistReg.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-ink/60 font-serif italic">
                    <Mail size={16} className="inline-block mr-2 -mt-0.5" />
                    No one is on the waitlist yet.
                  </td>
                </tr>
              )}
              {waitlistReg.map((r) => (
                <tr key={r.id} className="border-b border-ink/10 align-top" data-testid={`waitlist-reg-row-${r.id}`}>
                  <td className="py-4 pr-6 font-serif text-ink">{r.name}</td>
                  <td className="py-4 pr-6 text-sm text-ink/80">
                    <div>{r.phone}</div>
                    <div className="text-ink/60">{r.email}</div>
                  </td>
                  <td className="py-4 pr-6 text-sm text-ink/80 max-w-[28ch]">
                    <div>{r.address_line1}{r.address_line2 ? `, ${r.address_line2}` : ""}</div>
                    <div className="text-ink/60">{r.city}, {r.state} — {r.pincode}</div>
                  </td>
                  <td className="py-4 pr-6 font-serif italic text-sm text-ink/80 capitalize">
                    {(r.motherhood_stage || "").replace(/_/g, " ")}
                  </td>
                  <td className="py-4 pr-6 text-sm">
                    <span className={r.waitlist_consent ? "text-sage font-medium" : "text-ink/40"}>
                      {r.waitlist_consent ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-4 pr-6 text-xs text-ink/60">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                  </td>
                  <td className="py-4 pr-6">
                    <button
                      onClick={() => approveWaitlist(r.id)}
                      className="btn-pill !py-1.5 !px-4 text-xs"
                      data-testid={`reg-approve-${r.id}`}
                    >
                      Approve for free envelope
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
